/**
 * Submission helper functions for Student Dashboard
 */

/**
 * Get current submission status for active content
 * @param {Object} userCurrentContent - Current content object
 * @param {string} currentContentType - Current content type
 * @param {Object} userSubmissionStatus - User submission status from Redux
 * @returns {Object|null} Submission status or null
 */
export const getCurrentSubmissionStatus = (userCurrentContent, currentContentType, userSubmissionStatus) => {
  if (!userCurrentContent || currentContentType !== 'submissionForm') {
    return null;
  }

  const status = userSubmissionStatus[userCurrentContent._id];
  return status || null;
};

/**
 * Check if current submission form allows progression
 * @param {string} currentContentType - Current content type
 * @param {Object} userCurrentContent - Current content object
 * @param {Object} userSubmissionStatus - User submission status from Redux
 * @returns {boolean} True if can progress from current submission
 */
export const canProgressFromSubmission = (currentContentType, userCurrentContent, userSubmissionStatus) => {
  if (currentContentType !== 'submissionForm') {
    return true; // Not a submission form, can progress
  }

  const submissionStatus = getCurrentSubmissionStatus(userCurrentContent, currentContentType, userSubmissionStatus);

  // If no submission yet, cannot progress
  if (!submissionStatus || !submissionStatus.hasSubmission) {
    return false;
  }

  // If automatic approval, can progress immediately
  if (submissionStatus.submission.isAutomatic) {
    return true;
  }

  // For manual approval, only allow progression if approved
  return submissionStatus.submission.status === 'approved';
};

/**
 * Validate URL input
 * @param {string} url - URL to validate
 * @returns {Object} Validation result { isValid, error }
 */
export const validateUrl = (url) => {
  if (!url || url.trim() === '') {
    return { isValid: true, error: '' };
  }

  try {
    new URL(url.trim());
    return { isValid: true, error: '' };
  } catch (error) {
    return {
      isValid: false,
      error: 'Please enter a valid URL (e.g., https://example.com)'
    };
  }
};

/**
 * Handle URL input change with validation
 * @param {string} value - New URL value
 * @param {Object} submissionData - Current submission data
 * @param {Function} setSubmissionData - State setter for submission data
 * @param {Function} setUrlError - State setter for URL error
 */
export const handleUrlChange = (value, submissionData, setSubmissionData, setUrlError) => {
  setSubmissionData({ ...submissionData, link: value });

  // Only validate if user has entered something
  if (value.trim() !== '') {
    // Add small delay to avoid validating while user is still typing
    setTimeout(() => {
      const validation = validateUrl(value);
      setUrlError(validation.error);
    }, 500);
  } else {
    setUrlError('');
  }
};

/**
 * Check if submission form data is valid
 * @param {Object} submissionData - Submission form data
 * @param {Object} userCurrentContent - Current content object
 * @returns {Object} Validation result { isValid, errors }
 */
export const validateSubmissionData = (submissionData, userCurrentContent) => {
  const errors = {};

  // Check if URL is required and valid
  if (userCurrentContent?.linkRequired) {
    if (!submissionData.link || submissionData.link.trim() === '') {
      errors.link = 'URL is required for this submission';
    } else {
      const urlValidation = validateUrl(submissionData.link);
      if (!urlValidation.isValid) {
        errors.link = urlValidation.error;
      }
    }
  }

  // Check if comment is required
  if (userCurrentContent?.commentRequired) {
    if (!submissionData.comment || submissionData.comment.trim() === '') {
      errors.comment = 'Comment is required for this submission';
    }
  }

  // Check if achievement image is required
  if (userCurrentContent?.achievementImageRequired) {
    if (!submissionData.achievementImages || submissionData.achievementImages.length === 0) {
      errors.achievementImages = 'At least one achievement image is required for this submission';
    }
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

/**
 * Prepare submission data for API call
 * @param {Object} submissionData - Raw submission data
 * @param {Object} userCurrentContent - Current content object
 * @param {Object} user - User object
 * @returns {Object} Prepared submission data
 */
export const prepareSubmissionData = (submissionData, userCurrentContent, user) => {
  const userId = user.id || user._id;

  return {
    submissionForm: userCurrentContent._id,
    link: submissionData.link?.trim() || '',
    comment: submissionData.comment?.trim() || '',
    achievementImages: submissionData.achievementImages || [],
    student: userId
  };
};